import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { PostRequest } from "@/lib/http";
import { toast } from "sonner";
// import { useMediaQuery } from "react-responsive";
import { useDispatch } from "react-redux";
import { loginUser } from "@/redux/userReducer";
import { FcGoogle } from "react-icons/fc";
import { SocialLogin } from "@capgo/capacitor-social-login";

const LoginForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const navigate = useNavigate();
  // const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();

  const dispatch = useDispatch();

  const onSubmit = async (data) => {
    const { data: loginData, err } = await PostRequest({
      url: "/api/login",
      body: data,
      setState: setLoading,
    });

    if (!err) {
      // toast.success("Login successful", {
      //   description: `Welcome back`,
      // });
      localStorage.setItem("user-token", loginData?.token);
      dispatch(loginUser(loginData?.user));
      navigate("/dashboard");
    } else {
      toast.error("Login Error", {
        description: err,
      });
    }
  };

  const googleSignIn = async () => {
    try {
      setGoogleLoading(true);
      const res = await SocialLogin.login({
        provider: "google",
        options: {},
      });

      // handle the response
      const body = res.result?.profile;
      if (body) {
        //TODO: Send the data
        const { data, err } = await PostRequest({
          url: "/api/google-login",
          body,
          setState: setLoading,
        });
        if (!err) {
          localStorage.setItem("user-token", data?.token);
          dispatch(loginUser(data?.user));
          navigate("/dashboard");
        } else {
          toast.error("Login Error", {
            description: err,
          });
        }
      }
    } catch (error) {
      console.error("Google Sign-In Error:", error);
    } finally {
      setGoogleLoading(false);
    }
  };

  useEffect(() => {
    SocialLogin.initialize({
      google: {
        webClientId:
          "454824576589-kt773ve6thmbccf5kt9ut0a4hc9kigqo.apps.googleusercontent.com",
      },
    });
  }, []);

  return (
    <motion.form
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
      onSubmit={handleSubmit(onSubmit)}
      className="mt-5 flex flex-col gap-3"
    >
      <div className="flex flex-col gap-1">
        <label htmlFor="email" className="text-base font-medium">
          Email Address
        </label>
        <input
          id="email"
          type="text"
          {...register("email", {
            required: "Email is required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address",
            },
          })}
          className="inputClass"
        />
        {errors.email && (
          <span className="text-sm font-medium text-red-500">
            {errors.email.message}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-base font-medium">Password</label>
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            {...register("password", {
              required: "Password is required",
              minLength: {
                value: 8,
                message: "Password must be at least 8 characters",
              },
            })}
            className="inputClass"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute top-1/2 right-3 -translate-y-1/2"
          >
            {showPassword ? (
              <EyeOff size={20} className="text-gray-500" />
            ) : (
              <Eye size={20} className="text-gray-500" />
            )}
          </button>
        </div>

        {errors.password && (
          <p className="text-sm font-medium text-red-500">
            {errors.password.message}
          </p>
        )}
      </div>

      <p className="text-sm text-gray-500">
        By signing up or logging in, you consent to Billzpay&apos;s{" "}
        <a
          href="https://www.billzpay.com.ng/TOS"
          target="_blank"
          className="font-medium text-black dark:text-white"
        >
          Terms of Use
        </a>{" "}
        and{" "}
        <a
          href="https://www.billzpay.com.ng/privacy-policy"
          target="_blank"
          className="font-medium text-black dark:text-white"
        >
          Privacy Policy
        </a>{" "}
      </p>

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={isSubmitting}
          className={`btn $ w-full rounded-md px-8 py-2.5 font-medium text-white transition-all duration-150 disabled:pointer-events-none`}
        >
          {loading ? (
            <div className="flex items-center justify-center gap-2">
              <Loader2 color="#fff" size={20} className="animate-spin" />
              Authenticating...
            </div>
          ) : (
            "Login"
          )}
        </button>
      </div>

      <div className="flex w-full justify-between">
        <Link
          to="/reset-password"
          className="flex justify-end text-sm font-medium hover:underline"
        >
          Forget password?
        </Link>{" "}
        <Link
          to="/sign-up"
          className="flex justify-end text-sm font-semibold hover:underline"
        >
          Sign up
        </Link>
      </div>

      <div className="my-1 flex items-center gap-3">
        <div className="h-[1px] w-full bg-gray-300 dark:bg-gray-600"></div>
        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
          OR
        </span>
        <div className="h-[1px] w-full bg-gray-300 dark:bg-gray-600"></div>
      </div>

      <a
        className="dark:bg-darkInput flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-8 py-2.5 text-sm font-medium text-gray-700 transition-all duration-150 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-800"
        // href="https://backend-new.billzpay.com.ng/google/login"
        onClick={googleSignIn}
      >
        {googleLoading ? (
          <Loader2 className="animate-spin" size={20} />
        ) : (
          <>
            <FcGoogle size={20} />
            Continue with Google
          </>
        )}
      </a>
    </motion.form>
  );
};

export default LoginForm;
